export interface PortalDocument {
  id: string;
  name: string;
  number: string;
  versionLabel: string;
  type: 'Policy' | 'Procedure' | 'File';
  category: string;
  categories: string[];
  updatedAt: string | null;
  summary: string;
  formId: string | null;
  fileUrl: string | null;
}

export interface CategoryChip {
  name: string;
  count: number;
  icon: string;
}

const CATEGORY_ICONS: Record<string, string> = {
  Personnel: 'groups',
  Finance: 'account_balance',
  'IT & Security': 'shield',
  'Public Works': 'construction',
  'Public Records': 'gavel',
  General: 'folder',
};

const LEADING_SKIP = new Set(['city', 'of', 'the', 'and', 'town', 'village']);

export function documentSlug(name: string, fallbackId: string): string {
  const base = (name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
  return base || fallbackId;
}

export function documentSlugs(documents: PortalDocument[]): Map<string, PortalDocument> {
  const bySlug = new Map<string, PortalDocument>();
  for (const document of documents) {
    const id = document.formId || document.id;
    const base = documentSlug(document.name, id);
    const idSuffix = id.replace(/[^a-z0-9]/gi, '').slice(0, 8).toLowerCase();
    const slug = bySlug.has(base) && idSuffix ? `${base}-${idSuffix}` : base;
    bySlug.set(slug, document);
  }
  return bySlug;
}

export function clientInitials(name: string): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  let start = 0;
  while (start < words.length - 1 && LEADING_SKIP.has(words[start].toLowerCase())) {
    start += 1;
  }
  const significant = words.slice(start);
  if (!significant.length) return 'P';
  if (significant.length === 1) return significant[0].slice(0, 2).toUpperCase();
  return significant.map((word) => (word === '&' ? '&' : word[0].toUpperCase())).join('');
}

export function formatVersion(version: string | null | undefined): string {
  const raw = (version || '').trim();
  if (!raw) return '';
  return /^v/i.test(raw) ? raw : `v${raw}`;
}

export function formatUpdated(
  iso: string | null | undefined,
  style: 'long' | 'short',
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions =
    style === 'long'
      ? { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return date.toLocaleDateString('en-US', options);
}

export function categoryIcon(name: string): string {
  return CATEGORY_ICONS[name] || 'folder';
}

export function toPortalLibrary(nodes: any[] | null | undefined): PortalDocument[] {
  const list = nodes || [];
  const documents: PortalDocument[] = [];
  const seen = new Set<string>();

  const push = (document: PortalDocument) => {
    const key = `${document.type}:${document.formId || document.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    documents.push(document);
  };

  for (const node of list) {
    if (node?.type === 'Policy' && node.policy) {
      const categories = categoriesFor(node, node.policy);
      push(fromForm(node.policy, 'Policy', node.id, categories, node.policyId || node.policy.id));
      for (const procedure of node.policy.procedures || []) {
        const procedureCategories = activeLabelNames(procedure);
        push(
          fromForm(
            procedure,
            'Procedure',
            procedure.id,
            procedureCategories.length ? procedureCategories : categories,
            procedure.id,
          ),
        );
      }
    } else if (node?.type === 'Procedure') {
      push(fromForm(node.procedure || node, 'Procedure', node.id, categoriesFor(node, node.procedure || node), node.id));
    } else if (node?.type === 'File') {
      const file = node.documents?.[0];
      const categories = categoriesFor(node);
      push({
        id: node.id,
        name: file?.documentName || node.name || 'File',
        number: '',
        versionLabel: '',
        type: 'File',
        category: categories[0],
        categories,
        updatedAt: file?.createdDate || null,
        summary: '',
        formId: null,
        fileUrl: file?.linkUrl || null,
      });
    }
  }

  return documents;
}

export function categoryChips(documents: PortalDocument[]): CategoryChip[] {
  const counts = new Map<string, number>();
  for (const document of documents) {
    for (const name of document.categories) {
      counts.set(name, (counts.get(name) || 0) + 1);
    }
  }
  const names = [...counts.keys()].sort((a, b) => {
    if (a === 'General') return 1;
    if (b === 'General') return -1;
    return a.localeCompare(b);
  });
  return [
    { name: 'All', count: documents.length, icon: 'apps' },
    ...names.map((name) => ({
      name,
      count: counts.get(name) || 0,
      icon: categoryIcon(name),
    })),
  ];
}

export const RECENT_WINDOW_DAYS = 90;

export function filterDocuments(
  documents: PortalDocument[],
  options: { search: string; category: string; recentOnly: boolean; now?: number },
): PortalDocument[] {
  const query = options.search.trim().toLowerCase();
  const now = options.now ?? Date.now();
  const filtered = documents.filter((document) => {
    if (options.recentOnly && !isRecentlyUpdated(document, now)) return false;
    if (options.category && options.category !== 'All' && document.categories.indexOf(options.category) === -1) {
      return false;
    }
    if (!query) return true;
    return [document.name, document.number, document.versionLabel, ...document.categories]
      .some((value) => (value || '').toLowerCase().includes(query));
  });

  if (!options.recentOnly) return filtered;
  return [...filtered].sort((a, b) => timeOf(b) - timeOf(a));
}

export function recentlyUpdated(documents: PortalDocument[], limit = 3, now = Date.now()): PortalDocument[] {
  return documents
    .filter((document) => isRecentlyUpdated(document, now))
    .sort((a, b) => timeOf(b) - timeOf(a))
    .slice(0, limit);
}

export function isRecentlyUpdated(document: PortalDocument, now = Date.now()): boolean {
  const updated = timeOf(document);
  if (!updated) return false;
  const windowMs = RECENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return updated <= now && now - updated <= windowMs;
}

export function groupByCategory(documents: PortalDocument[]): { name: string; documents: PortalDocument[] }[] {
  const groups = new Map<string, PortalDocument[]>();
  for (const document of documents) {
    for (const name of document.categories) {
      const bucket = groups.get(name) || [];
      bucket.push(document);
      groups.set(name, bucket);
    }
  }
  return [...groups.keys()]
    .sort((a, b) => {
      if (a === 'General') return 1;
      if (b === 'General') return -1;
      return a.localeCompare(b);
    })
    .map((name) => ({ name, documents: groups.get(name) || [] }));
}

function fromForm(
  form: any,
  type: 'Policy' | 'Procedure',
  id: string,
  categories: string[],
  formId: string | null,
): PortalDocument {
  const updatedAt = form?.completedDate || form?.createdDate || null;
  const names = categories.length ? categories : ['General'];
  return {
    id,
    name: form?.name || 'Untitled',
    number: form?.number || '',
    versionLabel: formatVersion(form?.version),
    type,
    category: names[0],
    categories: names,
    updatedAt,
    summary: changeSummaryText(form?.aiChangeSummary),
    formId: formId || form?.id || null,
    fileUrl: null,
  };
}

function categoriesFor(node: any, form?: any): string[] {
  const fromNode = activeLabelNames(node);
  if (fromNode.length) return fromNode;
  const fromFormLabels = activeLabelNames(form);
  if (fromFormLabels.length) return fromFormLabels;
  return ['General'];
}

function activeLabelNames(source: any): string[] {
  const labels = Array.isArray(source?.labels) ? source.labels : [];
  return [...labels]
    .filter((label) => label?.isActive !== false && String(label?.name || '').trim())
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) ||
        String(a.name).localeCompare(String(b.name)),
    )
    .map((label) => String(label.name).trim());
}

function changeSummaryText(value: unknown): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  if (raw.charAt(0) !== '{') return raw;
  try {
    const parsed = JSON.parse(raw);
    const recommendation = String(
      parsed?.ReAcceptanceRecommendation || parsed?.reAcceptanceRecommendation || '',
    ).trim();
    if (recommendation) return recommendation;
  } catch {
    return raw;
  }
  return '';
}

function timeOf(document: PortalDocument): number {
  if (!document.updatedAt) return 0;
  const time = Date.parse(document.updatedAt);
  return Number.isNaN(time) ? 0 : time;
}

